import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { GEAR_RATIO_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';

/**
 * Gear ratio simulator.
 *
 * Two meshed gears: a fixed driver (motor) and a learner-picked driven gear.
 * Output RPM follows the standard ratio rule:
 *   outputRpm = driverRpm × (driverTeeth / drivenTeeth)
 *
 * Smaller driven gear → faster output. Larger driven gear → slower output.
 * Meshed external gears rotate in opposite directions.
 *
 * Content shape:
 * {
 *   instructions?: string,
 *   driver: { teeth: number, rpm: number, label?: string },
 *   gears:  Array<{ id: string, teeth: number, label?: string }>,  // driven options
 *   target?: { rpm: number, tolerance?: number, label?: string },  // omit for explore mode
 *   mode?: 'target' | 'explore',                                   // inferred from target
 *   victory?: { message?: string },
 * }
 *
 * Guided shell (`guidedContext.singleSurface` + tap-first `lessonUiMode`): compact gear stage, 2-column
 * driven-gear grid when there are 3+ options, and shorter tips on narrow viewports.
 * With `onRegisterLessonFooterActions`, target mode uses footer **Check**: pick a gear, then Check to see
 * output / feedback and to complete (no instant wrong/correct strip on tap alone).
 *
 * onComplete payload: {
 *   activityId,
 *   engine: 'gear_ratio',
 *   perfect: boolean,
 *   drivenTeeth?: number,
 *   outputRpm?: number,
 *   misses?: number
 * }
 */

const TOOTH_HEIGHT = 6;
const TOOTH_THICKNESS = 3;

function GearSvg({ teeth, size, color, accent, gearRef, ariaLabel }) {
  const r = size / 2 - 4;
  const inner = r * 0.55;
  const marks = [];
  for (let i = 0; i < teeth; i += 1) {
    const angle = (i / teeth) * 360;
    marks.push(
      <rect
        key={i}
        x={-TOOTH_THICKNESS / 2}
        y={-r}
        width={TOOTH_THICKNESS}
        height={TOOTH_HEIGHT}
        fill={color}
        transform={`rotate(${angle})`}
      />,
    );
  }
  return (
    <svg
      viewBox={`-${size / 2} -${size / 2} ${size} ${size}`}
      ref={gearRef}
      role="img"
      aria-label={ariaLabel}
      style={{
        display: 'block',
        width: '100%',
        maxWidth: size,
        height: 'auto',
        aspectRatio: '1',
        willChange: 'transform',
      }}
    >
      <circle r={r - 3} fill={color} opacity={0.18} />
      <g>{marks}</g>
      <circle r={inner} fill={color} opacity={0.92} />
      <circle r={inner * 0.28} fill={accent || '#fff'} />
      <circle r={inner * 0.08} fill={color} />
    </svg>
  );
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function formatRpm(rpm) {
  if (!Number.isFinite(rpm)) return '0';
  if (Math.abs(rpm) < 10) return rpm.toFixed(1);
  return Math.round(rpm).toString();
}

export default function GearRatioEngine({
  title = 'Gear ratio',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_gear_ratio',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  guidedContext,
  onRegisterLessonFooterActions,
}) {
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });

  const driver = content.driver || { teeth: 24, rpm: 60, label: 'Motor' };
  const gears = Array.isArray(content.gears) ? content.gears : [];
  const target = content.target || null;
  const mode = content.mode || (target ? 'target' : 'explore');
  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const shellTargetDefer =
    typeof onRegisterLessonFooterActions === 'function' && mode === 'target' && target != null;

  const [pickedId, setPickedId] = useState(() => (shellTargetDefer ? null : gears[0]?.id ?? null));
  /** After footer Check: show output rpm, target strip styling, and too fast/slow tip (shell target mode only). */
  const [evalVisible, setEvalVisible] = useState(() => !shellTargetDefer);
  const [completed, setCompleted] = useState(false);
  const [misses, setMisses] = useState(0);
  const [a11yMsg, setA11yMsg] = useState('');
  const [showCelebration, celebrate] = useCelebration();

  const driverRef = useRef(null);
  const drivenRef = useRef(null);
  const driverAngleRef = useRef(0);
  const drivenAngleRef = useRef(0);
  const lastTickRef = useRef(0);
  const lockSinceRef = useRef(null);
  const triedSetRef = useRef(new Set());
  const completedRef = useRef(false);

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: GEAR_RATIO_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: completed,
    enabled: guidedHintsEnabled,
  });

  const { noteUserAction, noteIncorrectAnswer, noteCorrectAnswer } = guided;

  /** Guided lesson shell on phone: tighter layout so target strip + gear pair + all options fit above the footer. */
  const compactGear = tapFirst && singleSurface;
  const narrowGear = compactGear && guided.isNarrow;

  const driven = useMemo(() => gears.find((g) => g.id === pickedId) || null, [gears, pickedId]);
  const outputRpm = useMemo(() => {
    if (!driven || !driven.teeth) return 0;
    return (Number(driver.rpm) * Number(driver.teeth)) / Number(driven.teeth);
  }, [driver.rpm, driver.teeth, driven]);

  const tolerance = Number(target?.tolerance ?? 1);
  const inTarget = useMemo(() => {
    if (!target || !Number.isFinite(target.rpm)) return false;
    return Math.abs(outputRpm - target.rpm) <= tolerance;
  }, [outputRpm, target, tolerance]);

  const showEval = !shellTargetDefer || evalVisible;
  /** Shell + Check: selection is committed visually only after Check — hide driven rpm text and keep driven gear from spinning until then. */
  const pendingShellCheck = shellTargetDefer && !evalVisible && !completed;
  const animOutputRpm = pendingShellCheck ? 0 : outputRpm;

  const gearsSig = useMemo(() => gears.map((g) => g.id).join('|'), [gears]);

  const completeTargetSuccess = useCallback(
    (drivenGear, rpm, missMode) => {
      if (completedRef.current) return;
      completedRef.current = true;
      const raw = triedSetRef.current.size;
      const totalMisses =
        missMode === 'minusWinningPick' ? Math.max(0, raw - 1) : Math.max(0, raw);
      setMisses(totalMisses);
      setCompleted(true);
      noteCorrectAnswer();
      celebrate();
      setA11yMsg('Locked in! Activity complete.');
      trackMiniAppEvent.interaction(activityId, 'correct');
      trackMiniAppEvent.completed(activityId);
      onComplete?.({
        activityId,
        engine: 'gear_ratio',
        perfect: totalMisses === 0,
        drivenTeeth: drivenGear?.teeth,
        outputRpm: rpm,
        misses: totalMisses,
      });
    },
    [activityId, celebrate, noteCorrectAnswer, onComplete],
  );

  useEffect(() => {
    completedRef.current = false;
    setCompleted(false);
    setEvalVisible(!shellTargetDefer);
    setPickedId(shellTargetDefer ? null : gears[0]?.id ?? null);
    triedSetRef.current = new Set();
    lockSinceRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when activity identity or defer mode changes
  }, [gearsSig, shellTargetDefer, activityId]);

  // Animate both gears with rAF; pause briefly when paused/completed.
  useEffect(() => {
    let raf;
    const tick = (t) => {
      if (!lastTickRef.current) lastTickRef.current = t;
      const dt = clamp((t - lastTickRef.current) / 1000, 0, 0.1);
      lastTickRef.current = t;
      driverAngleRef.current += (Number(driver.rpm) / 60) * 360 * dt;
      drivenAngleRef.current -= (animOutputRpm / 60) * 360 * dt;
      if (driverRef.current) {
        driverRef.current.style.transform = `rotate(${driverAngleRef.current.toFixed(2)}deg)`;
      }
      if (drivenRef.current) {
        drivenRef.current.style.transform = `rotate(${drivenAngleRef.current.toFixed(2)}deg)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      lastTickRef.current = 0;
      cancelAnimationFrame(raf);
    };
  }, [driver.rpm, animOutputRpm]);

  // Track which gears were tried (non-shell: every pick; shell: wrong Check only — see commitCheck).
  useEffect(() => {
    if (!pickedId || shellTargetDefer) return;
    triedSetRef.current.add(pickedId);
    onProgress?.({ activityId, engine: 'gear_ratio', phase: 'select', drivenId: pickedId });
  }, [pickedId, activityId, onProgress, shellTargetDefer]);

  // Lock-in: if in-target for ~900 ms, complete (demo / no lesson shell only).
  useEffect(() => {
    if (shellTargetDefer || mode !== 'target' || completedRef.current) return undefined;
    if (!inTarget) {
      lockSinceRef.current = null;
      return undefined;
    }
    lockSinceRef.current = performance.now();
    setA11yMsg('On target — hold steady.');
    const id = window.setTimeout(() => {
      if (lockSinceRef.current && performance.now() - lockSinceRef.current >= 850 && !completedRef.current) {
        completeTargetSuccess(driven, outputRpm, 'minusWinningPick');
      }
    }, 900);
    return () => window.clearTimeout(id);
  }, [inTarget, mode, activityId, driven, outputRpm, celebrate, onComplete, noteCorrectAnswer, shellTargetDefer, completeTargetSuccess]);

  const commitCheck = useCallback(() => {
    if (!shellTargetDefer || completed || pickedId == null || !target) return;
    const g = gears.find((x) => x.id === pickedId);
    if (!g?.teeth) return;
    noteUserAction();
    setEvalVisible(true);
    const rpm = (Number(driver.rpm) * Number(driver.teeth)) / Number(g.teeth);
    const ok = Number.isFinite(rpm) && Math.abs(rpm - target.rpm) <= tolerance;
    onProgress?.({ activityId, engine: 'gear_ratio', phase: 'check', drivenId: pickedId, lastCorrect: ok });
    if (ok) {
      completeTargetSuccess(g, rpm, 'wrongChecksOnly');
    } else {
      triedSetRef.current.add(pickedId);
      noteIncorrectAnswer();
      setA11yMsg('Not quite — try another gear, then tap Check.');
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  }, [
    shellTargetDefer,
    completed,
    pickedId,
    target,
    gears,
    driver.rpm,
    driver.teeth,
    noteUserAction,
    noteIncorrectAnswer,
    activityId,
    onProgress,
    tolerance,
    completeTargetSuccess,
  ]);

  const revealAnswer = useCallback(() => {
    if (completed || !target || gears.length === 0) return;
    noteUserAction();
    const sorted = [...gears].sort((a, b) => Number(a.teeth) - Number(b.teeth));
    let best = sorted[0];
    let bestDiff = Infinity;
    for (const cand of gears) {
      if (!cand.teeth) continue;
      const rpm = (Number(driver.rpm) * Number(driver.teeth)) / Number(cand.teeth);
      const d = Math.abs(rpm - target.rpm);
      if (d < bestDiff) {
        bestDiff = d;
        best = cand;
      }
    }
    if (!best?.id) return;
    setPickedId(best.id);
    setEvalVisible(true);
    const rpm = (Number(driver.rpm) * Number(driver.teeth)) / Number(best.teeth);
    if (Number.isFinite(rpm) && Math.abs(rpm - target.rpm) <= tolerance) {
      completeTargetSuccess(best, rpm, 'wrongChecksOnly');
    }
  }, [target, gears, driver.rpm, driver.teeth, noteUserAction, completeTargetSuccess, tolerance, completed]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (completed || gears.length === 0) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    if (shellTargetDefer) {
      onRegisterLessonFooterActions({
        checkAnswer: commitCheck,
        checkAnswerDisabled: pickedId == null,
        showAnswer: revealAnswer,
      });
      return () => onRegisterLessonFooterActions(null);
    }
    onRegisterLessonFooterActions({ showAnswer: revealAnswer });
    return () => onRegisterLessonFooterActions(null);
  }, [
    onRegisterLessonFooterActions,
    completed,
    gears.length,
    shellTargetDefer,
    pickedId,
    commitCheck,
    revealAnswer,
  ]);

  const pickDrivenGear = (g) => {
    if (completed) return;
    if (shellTargetDefer) {
      setPickedId(g.id);
      setEvalVisible(false);
      return;
    }
    noteUserAction();
    if (mode === 'target' && target && pickedId !== g.id) {
      const nextDriven = gears.find((x) => x.id === g.id);
      const nextRpm =
        nextDriven && nextDriven.teeth
          ? (Number(driver.rpm) * Number(driver.teeth)) / Number(nextDriven.teeth)
          : NaN;
      const nextInTarget = Number.isFinite(nextRpm) && Math.abs(nextRpm - target.rpm) <= tolerance;
      if (!nextInTarget) noteIncorrectAnswer();
    }
    setPickedId(g.id);
  };

  const finishExplore = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setCompleted(true);
    noteCorrectAnswer();
    celebrate();
    trackMiniAppEvent.completed(activityId);
    onComplete?.({
      activityId,
      engine: 'gear_ratio',
      perfect: true,
      drivenTeeth: driven?.teeth,
      outputRpm,
    });
  };

  // Visual size scales with tooth count so big gears look big; compact mode caps height for small viewports.
  const baseDriver = compactGear ? (narrowGear ? 80 : 92) : 120;
  const drivenMin = compactGear ? 52 : 70;
  const drivenMax = compactGear ? (narrowGear ? 104 : 118) : 180;
  const driverSize = baseDriver;
  const drivenSize = driven
    ? clamp(baseDriver * Math.sqrt(driven.teeth / Math.max(1, driver.teeth)), drivenMin, drivenMax)
    : Math.round(baseDriver * 0.85);

  const useChoiceGrid = compactGear && gears.length >= 3;

  // Tip / feedback: lesson shell + target mode only after footer Check (showEval).
  let tip = '';
  if (target && driven && !completed && showEval) {
    if (inTarget) {
      tip = shellTargetDefer ? '' : 'Locked in! Hold steady…';
    } else if (narrowGear) {
      tip =
        outputRpm > target.rpm
          ? `Too fast (${formatRpm(outputRpm)} rpm). Try a larger gear.`
          : `Too slow (${formatRpm(outputRpm)} rpm). Try a smaller gear.`;
    } else if (outputRpm > target.rpm) {
      tip = `Too fast (${formatRpm(outputRpm)} rpm). Try a larger driven gear to slow it down.`;
    } else {
      tip = `Too slow (${formatRpm(outputRpm)} rpm). Try a smaller driven gear to speed it up.`;
    }
  }

  return (
    <div
      style={{
        ...getEngineRootStyle({ singleSurface }),
        position: 'relative',
        ...(guided.contentReserveStyle || {}),
        ...(compactGear ? { overflow: 'hidden', minHeight: 0 } : {}),
      }}
    >
      <CelebrationOverlay visible={showCelebration} message={content.victory?.message || 'Nailed it!'} />
      <A11yAnnouncement message={a11yMsg} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!suppressActivityTitle ? (
        <h2 style={{ ...titleStyle, margin: compactGear ? '0 0 4px' : titleStyle.margin }}>{title}</h2>
      ) : null}
      {!tapFirst && content.instructions && <p style={instructionsStyle}>{content.instructions}</p>}

      {target ? (
        <div
          aria-live="polite"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: compactGear ? 6 : 10,
            padding: compactGear ? '6px 10px' : '8px 12px',
            borderRadius: compactGear ? 10 : 12,
            background:
              inTarget && showEval && !completed ? 'rgba(34,197,94,0.16)' : 'var(--color-bg)',
            border: `1px solid ${
              inTarget && showEval && !completed ? 'rgba(34,197,94,0.45)' : 'var(--color-border)'
            }`,
            marginBottom: compactGear ? 8 : 14,
            fontSize: compactGear ? 13 : 14,
            fontWeight: 700,
            color: 'var(--color-text)',
          }}
        >
          <span>
            Target: {target.rpm} rpm
            <span style={{ fontWeight: 500, opacity: 0.7 }}> (±{tolerance})</span>
          </span>
          <span>Output: {showEval && driven ? `${formatRpm(outputRpm)} rpm` : '—'}</span>
        </div>
      ) : (
        <div
          style={{
            padding: compactGear ? '6px 10px' : '8px 12px',
            borderRadius: compactGear ? 10 : 12,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            marginBottom: compactGear ? 8 : 14,
            fontSize: compactGear ? 13 : 14,
            fontWeight: 700,
            color: 'var(--color-text)',
          }}
        >
          Output: {driven ? `${formatRpm(outputRpm)} rpm` : 'Pick a driven gear'}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compactGear ? 10 : 18,
          rowGap: compactGear ? 10 : 20,
          padding: compactGear ? '8px 6px' : '14px 8px',
          background: 'var(--color-bg)',
          borderRadius: compactGear ? 12 : 16,
          border: '1px solid var(--color-border)',
          marginBottom: compactGear ? 8 : 14,
          minHeight: compactGear ? 0 : 200,
        }}
      >
        <div style={{ textAlign: 'center', flex: '1 1 120px', maxWidth: '100%' }}>
          <GearSvg
            teeth={driver.teeth}
            size={driverSize}
            color="var(--color-primary)"
            accent="#fff"
            gearRef={driverRef}
            ariaLabel={`Driver gear with ${driver.teeth} teeth at ${driver.rpm} rpm`}
          />
          <div style={{ marginTop: compactGear ? 4 : 6, fontSize: compactGear ? 12 : 13, fontWeight: 700 }}>
            {driver.label || 'Motor'}
          </div>
          <div style={{ fontSize: compactGear ? 11 : 12, opacity: 0.7 }}>
            {driver.teeth}T · {driver.rpm} rpm
          </div>
        </div>
        <div aria-hidden style={{ fontSize: compactGear ? 18 : 22, opacity: 0.55, flex: '0 0 auto' }}>
          ⟶
        </div>
        <div style={{ textAlign: 'center', flex: '1 1 120px', maxWidth: '100%' }}>
          {driven ? (
            <GearSvg
              teeth={driven.teeth}
              size={drivenSize}
              color="#22c55e"
              accent="#fff"
              gearRef={drivenRef}
              ariaLabel={
                pendingShellCheck
                  ? `Driven gear with ${driven.teeth} teeth — tap Check to see output speed`
                  : `Driven gear with ${driven.teeth} teeth running at ${formatRpm(outputRpm)} rpm`
              }
            />
          ) : (
            <div
              style={{
                width: '100%',
                maxWidth: drivenSize,
                aspectRatio: '1',
                borderRadius: '50%',
                border: '2px dashed var(--color-border)',
                opacity: 0.7,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: compactGear ? 11 : 12,
                fontWeight: 600,
                color: 'var(--color-text)',
                margin: '0 auto',
              }}
            >
              Pick a gear
            </div>
          )}
          <div style={{ marginTop: compactGear ? 4 : 6, fontSize: compactGear ? 12 : 13, fontWeight: 700 }}>
            {driven?.label || 'Driven'}
          </div>
          <div style={{ fontSize: compactGear ? 11 : 12, opacity: 0.7 }}>
            {driven
              ? pendingShellCheck
                ? `${driven.teeth}T · —`
                : `${driven.teeth}T · ${formatRpm(outputRpm)} rpm`
              : '—'}
          </div>
        </div>
      </div>

      <div
        role="group"
        aria-label="Gear options"
        style={
          useChoiceGrid
            ? {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: narrowGear ? 6 : 8,
                marginBottom: compactGear ? 8 : 12,
              }
            : {
                display: 'flex',
                flexDirection: tapFirst ? 'column' : 'row',
                flexWrap: tapFirst ? 'nowrap' : 'wrap',
                gap: tapFirst ? 10 : 8,
                marginBottom: 12,
              }
        }
      >
        {gears.map((g) => {
          const active = pickedId === g.id;
          return (
            <button
              key={g.id}
              type="button"
              disabled={completed}
              onClick={() => pickDrivenGear(g)}
              aria-pressed={active}
              style={{
                ...secondaryBtn,
                width: tapFirst || useChoiceGrid ? '100%' : 'auto',
                flex: tapFirst && !useChoiceGrid ? '0 0 auto' : '1 1 130px',
                minHeight: compactGear ? 44 : tapFirst ? 56 : 48,
                padding: compactGear ? '8px 10px' : tapFirst ? '14px 16px' : '8px 12px',
                borderColor: active ? 'var(--color-primary)' : 'var(--color-border)',
                borderWidth: active ? 3 : 2,
                background: active ? 'rgba(99,102,241,0.16)' : 'var(--color-bg)',
                fontWeight: 700,
                fontSize: compactGear ? 14 : tapFirst ? 16 : 14,
              }}
            >
              {g.teeth}T
              {g.label ? <span style={{ fontWeight: 500, opacity: 0.75 }}> · {g.label}</span> : null}
            </button>
          );
        })}
      </div>

      {tip && !completed ? (
        <div
          style={{
            ...(inTarget ? feedbackCorrect : feedbackIncorrect),
            ...(tapFirst
              ? {
                  fontSize: narrowGear ? 12 : 13,
                  lineHeight: 1.35,
                  maxHeight: narrowGear ? '3.2em' : '4.5em',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: narrowGear ? 2 : 3,
                  WebkitBoxOrient: 'vertical',
                }
              : {}),
          }}
        >
          {tip}
        </div>
      ) : null}

      {completed ? (
        <div style={{ ...feedbackCorrect, marginTop: compactGear ? 6 : 8 }}>
          {content.victory?.message || 'Nice — gears do that perfectly.'}
        </div>
      ) : null}

      {mode === 'explore' && !completed ? (
        <button type="button" style={{ ...primaryBtn, marginTop: 12 }} onClick={finishExplore}>
          Done exploring
        </button>
      ) : null}

      {/* misses / used gears counter for screen readers */}
      <span style={{ position: 'absolute', left: -9999 }} aria-live="polite">
        {misses > 0 ? `Tried ${misses + 1} gears so far.` : ''}
      </span>
    </div>
  );
}
